import { useMutation } from '@tanstack/react-query';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { AssistQueryClient, useQueryAssist } from 'services/assist-api/query-client';
import { preparePostOrPatchTestInstanceResponseMarkItem } from './hooks.helpers';
import type {
  BaseTestInstanceResponseMarkItem,
  PatchTestInstanceResponseMarkItem,
  PostTestInstanceResponseMarkItem
} from './types';

const groups = [
  'testinstanceresponseitem-submittedvalue',
  'testinstanceresponseitem-submittedoptions'
];
const include = 'submittedOptions';

/**
 * Fetch test-instance-response-mark-items
 * @param params can be used to customise/limit the test-instance-response-mark-items retrieved
 * @returns the requested test-instance-response-mark-items
 */
export const getTestInstanceResponseMarkItems: GetMany<BaseTestInstanceResponseMarkItem> = async (
  params
) =>
  (
    await ASSIST_API.get('test-instance-response-mark-items', {
      params: requestParams({}, params)
    })
  ).data;

/**
 * Fetch test-instance-response-mark-item (singular)
 * @param params can be used to customise/limit the test-instance-response-mark-item retrieved
 * @returns a single test-instance-response-mark-item
 */
const getTestInstanceResponseMarkItem: GetOne<BaseTestInstanceResponseMarkItem> = async (
  id,
  params
) => (await ASSIST_API.get(`test-instance-response-mark-items/${id}`, { params })).data;

/**
 * Hook implementation to fetch test-instance-response-mark-items
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns test-instance-response-mark-items matching request
 */
export const useTestInstanceResponseMarkItems: AssistQuery.UseMany<
  BaseTestInstanceResponseMarkItem
> = (params, options) =>
  useQueryAssist(
    ['test-instance-response-mark-items', params],
    () => getTestInstanceResponseMarkItems(params),
    options
  );

/**
 * Given an testInstanceResponseMarkItemId fetch and return the test instance response mark item
 *  - only runs once testInstanceResponseMarkItemId is provided
 *  - fetches the one testInstanceResponseMarkItem
 *  - caches data
 * @param id testInstanceResponseMarkItemId
 * @param params optional parameters to change requested data
 * @returns the matching testInstanceResponseMarkItem
 */
export const useTestInstanceResponseMarkItem: AssistQuery.UseOne<
  BaseTestInstanceResponseMarkItem
> = (id, params, options) =>
  useQueryAssist(
    ['test-instance-response-mark-items', { id, ...params }],
    () => (id ? getTestInstanceResponseMarkItem(id, params) : undefined),
    {
      ...options,
      enabled: !!id
    }
  );

/**
 * Create new test-instance-response-mark-items
 * - takes an array of options creating a POST request per item
 * @param options
 */
export const usePostTestInstanceResponseMarkItems: AssistQuery.UseMutations<
  undefined,
  PostTestInstanceResponseMarkItem[]
> = (options) =>
  useMutation(
    (dataArray: PostTestInstanceResponseMarkItem[]) =>
      Promise.allSettled(
        dataArray.map((data) =>
          ASSIST_API.post(
            'test-instance-response-mark-items',
            preparePostOrPatchTestInstanceResponseMarkItem(data),
            {
              params: requestParams({ groups, include })
            }
          )
        )
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['test-instance-response-mark-items']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

/**
 * Update test-instance-response-mark-items
 * - takes an array of options creating a PATCH request per item
 * @param options
 */
export const usePatchTestInstanceResponseMarkItems: AssistQuery.UseMutations<
  undefined,
  PatchTestInstanceResponseMarkItem[]
> = (options) =>
  useMutation(
    (dataArray: PatchTestInstanceResponseMarkItem[]) =>
      Promise.allSettled(
        dataArray.map(({ id, data }) =>
          ASSIST_API.patch(
            `test-instance-response-mark-items/${id}`,
            preparePostOrPatchTestInstanceResponseMarkItem(data),
            {
              params: requestParams({ groups, include })
            }
          )
        )
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['test-instance-response-mark-items']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

/**
 * Remove test-instance-response-mark-items
 * - takes an array of ids creating a DELETE request per item
 * @param options
 */
export const useDeleteTestInstanceResponseMarkItems: AssistQuery.UseMutations<
  undefined,
  number[]
> = (options) =>
  useMutation(
    (ids: number[]) =>
      Promise.allSettled(
        ids.map((id) => ASSIST_API.delete(`test-instance-response-mark-items/${id}`))
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['test-instance-response-mark-items']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );
